/**
 * GLOBAL UPLOAD STORE (Zustand)
 * Survives SPA navigation — uploads continue across page changes.
 * Persists state to big_data_datasets table in Supabase for resume after disconnect.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

// ───────────── Types ─────────────

export interface UploadSessionState {
  datasetId?: string;          // big_data_datasets.id (Supabase row)
  uploadId: string;            // S3 multipart upload ID
  s3Key: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number;
  completedParts: Array<{ PartNumber: number; ETag: string }>;
  uploadStatus: 'uploading' | 'paused' | 'completing' | 'completed' | 'failed' | 'cancelled';
  bytesUploaded: number;
  uploadSpeed: number;
  estimatedTimeRemaining: number;
  uploadError?: string;
  createdAt: string;
}

interface UploadStore {
  // State
  activeUploads: Map<string, UploadSessionState>;
  pendingSessions: UploadSessionState[];
  isInitialized: boolean;

  // Actions
  registerUpload: (session: UploadSessionState) => void;
  updateProgress: (uploadId: string, update: Partial<UploadSessionState>) => void;
  markCompleted: (uploadId: string) => void;
  markFailed: (uploadId: string, error: string) => void;
  markPaused: (uploadId: string) => void;
  removeUpload: (uploadId: string) => void;
  loadPendingSessions: (userId: string) => Promise<void>;
  persistToSupabase: (uploadId: string) => Promise<void>;
  clearCompleted: () => void;

  // Getters
  getUpload: (uploadId: string) => UploadSessionState | undefined;
  hasActiveUploads: () => boolean;
}

// ───────────── Store ─────────────

export const useUploadStore = create<UploadStore>((set, get) => ({
  activeUploads: new Map(),
  pendingSessions: [],
  isInitialized: false,

  registerUpload: (session) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      newMap.set(session.uploadId, session);
      return { activeUploads: newMap };
    });
    logger.log(`[UploadStore] Registered upload: ${session.fileName} (${session.totalChunks} chunks)`);
  },

  updateProgress: (uploadId, update) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      const existing = newMap.get(uploadId);
      if (existing) {
        newMap.set(uploadId, { ...existing, ...update });
      }
      return { activeUploads: newMap };
    });
  },

  markCompleted: (uploadId) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      const existing = newMap.get(uploadId);
      if (existing) {
        newMap.set(uploadId, { ...existing, uploadStatus: 'completed' });
      }
      return { activeUploads: newMap };
    });
    logger.log(`[UploadStore] Upload completed: ${uploadId}`);
  },

  markFailed: (uploadId, error) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      const existing = newMap.get(uploadId);
      if (existing) {
        newMap.set(uploadId, { ...existing, uploadStatus: 'failed', uploadError: error });
      }
      return { activeUploads: newMap };
    });
    logger.log(`[UploadStore] Upload failed: ${uploadId} — ${error}`);
  },

  markPaused: (uploadId) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      const existing = newMap.get(uploadId);
      if (existing) {
        newMap.set(uploadId, { ...existing, uploadStatus: 'paused' });
      }
      return { activeUploads: newMap };
    });
    logger.log(`[UploadStore] Upload paused: ${uploadId}`);
  },

  removeUpload: (uploadId) => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      newMap.delete(uploadId);
      return {
        activeUploads: newMap,
        pendingSessions: state.pendingSessions.filter((s) => s.uploadId !== uploadId),
      };
    });
  },

  loadPendingSessions: async (userId) => {
    try {
      // Query big_data_datasets for rows with upload_status = 'uploading' or 'paused'
      const { data, error } = await supabase
        .from('big_data_datasets')
        .select('*')
        .eq('user_id', userId)
        .in('upload_status', ['uploading', 'paused'])
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('[UploadStore] Failed to load pending sessions:', error);
        set({ isInitialized: true });
        return;
      }

      if (data && data.length > 0) {
        const sessions: UploadSessionState[] = data.map((row: any) => ({
          datasetId: row.id,
          uploadId: row.upload_id,
          s3Key: row.s3_key,
          fileName: row.file_name,
          fileSize: row.file_size,
          chunkSize: row.chunk_size || 0,
          totalChunks: row.total_chunks || 0,
          uploadedChunks: row.uploaded_chunks || 0,
          completedParts: row.completed_parts || [],
          uploadStatus: row.upload_status,
          bytesUploaded: row.bytes_uploaded || 0,
          uploadSpeed: row.upload_speed || 0,
          estimatedTimeRemaining: 0,
          uploadError: row.upload_error,
          createdAt: row.created_at,
        }));

        set({ pendingSessions: sessions, isInitialized: true });
        logger.log(`[UploadStore] Loaded ${sessions.length} pending upload session(s)`);
      } else {
        set({ pendingSessions: [], isInitialized: true });
        logger.log('[UploadStore] No pending upload sessions found');
      }
    } catch (err) {
      logger.error('[UploadStore] Error loading sessions:', err);
      set({ isInitialized: true });
    }
  },

  persistToSupabase: async (uploadId) => {
    const session = get().activeUploads.get(uploadId);
    if (!session || !session.datasetId) return;

    try {
      const { error } = await supabase
        .from('big_data_datasets')
        .update({
          upload_id: session.uploadId,
          chunk_size: session.chunkSize,
          total_chunks: session.totalChunks,
          uploaded_chunks: session.uploadedChunks,
          completed_parts: session.completedParts,
          upload_status: session.uploadStatus,
          bytes_uploaded: session.bytesUploaded,
          upload_speed: session.uploadSpeed,
          upload_error: session.uploadError || null,
        })
        .eq('id', session.datasetId);

      if (error) {
        logger.error(`[UploadStore] Failed to persist session ${uploadId}:`, error);
      }
    } catch (err) {
      logger.error(`[UploadStore] Error persisting session ${uploadId}:`, err);
    }
  },

  clearCompleted: () => {
    set((state) => {
      const newMap = new Map(state.activeUploads);
      for (const [key, val] of newMap.entries()) {
        if (val.uploadStatus === 'completed' || val.uploadStatus === 'cancelled') {
          newMap.delete(key);
        }
      }
      return { activeUploads: newMap };
    });
  },

  getUpload: (uploadId) => get().activeUploads.get(uploadId),
  hasActiveUploads: () => {
    const uploads = get().activeUploads;
    for (const val of uploads.values()) {
      if (val.uploadStatus === 'uploading') return true;
    }
    return false;
  },
}));

// ───────────── beforeunload warning ─────────────

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (useUploadStore.getState().hasActiveUploads()) {
      e.preventDefault();
      e.returnValue = 'Un upload est en cours. Si vous quittez, il pourra être repris plus tard.';
      return e.returnValue;
    }
  });
}

export default useUploadStore;
